import React, { useEffect, useState, useMemo } from "react";
import { computeSavings } from "@/lib/simulation-defaults";
import { getSettings } from "@/lib/api";
import {
  makePdfT,
  pdfFmt,
  periodLabelKey,
  validatePdfTranslations,
} from "@/i18n/pdfI18n";

/**
 * PdfVisualReport
 * -----------------------------------------------------------------
 * Executive-summary variant of the sales report.
 *
 * Uses the dedicated PDF i18n system (`@/i18n/pdfI18n`). The
 * `overrideLang` prop is MANDATORY — the PDF is always rendered in
 * that specific language and no key is silently resolved through
 * another language.
 * -----------------------------------------------------------------
 */
export default function PdfVisualReport({ state, overrideLang, logoDataUrl }) {
  const lang = overrideLang;
  if (!lang) {
    // eslint-disable-next-line no-console
    console.error(
      "[PdfVisualReport] `overrideLang` is required and must be one of the supported PDF languages."
    );
  }
  useMemo(() => {
    if (!lang) return null;
    const v = validatePdfTranslations(lang);
    if (!v.ok) {
      // eslint-disable-next-line no-console
      console.error(
        `[PdfVisualReport] Missing PDF translations for language "${lang}":`,
        v.missing
      );
    }
    return v;
  }, [lang]);

  const tPdf = useMemo(() => (lang ? makePdfT(lang) : () => ""), [lang]);
  const fmt = useMemo(
    () => ({
      money: (v) => pdfFmt.money(v, lang),
      money0: (v) => pdfFmt.money0(v, lang),
      percent: (v, d = 1) => pdfFmt.percent(v, lang, d),
      hours: (v) => pdfFmt.hours(v, lang),
      minAndHours: (v) => pdfFmt.minAndHours(v, lang),
      date: (d) => pdfFmt.date(d, lang),
      num: (v, o) => pdfFmt.number(v, lang, o),
    }),
    [lang]
  );

  const { client, trailer, customer, primo } = state;
  const thickness = Number(trailer.profile_thickness_mm) || 85;
  const s = computeSavings(state);
  const { cb, pb, ct, pt, doors } = s;

  const priceCurrent = s.priceCustomer;
  const pricePrimo = s.pricePrimo;
  const priceDelta = s.priceSavings;
  const reductionPct = s.priceReductionPct;
  const timeCurrentMin = s.timeCustomerMin;
  const timePrimoMin = s.timePrimoMin;
  const timeDeltaH = s.timeSavingsH;
  const timeReductionPct = s.timeReductionPct;
  const periodSavings = s.periodSavings;
  const annualSavings = s.annualSavings;
  const quantity = s.quantity;
  const period = s.period;
  const periodLabel = tPdf(periodLabelKey(period, "label"));
  const periodLower = tPdf(periodLabelKey(period, "lower"));

  const refId = state.id ? state.id.slice(0, 8).toUpperCase() : "DRAFT";

  const [techSettings, setTechSettings] = useState({
    tech_name: "",
    tech_email: "",
    tech_phone: "",
    tech_greeting: "",
    tech_signature_image: "",
  });
  useEffect(() => {
    getSettings().then(setTechSettings).catch(() => {});
  }, []);
  const hasTech =
    techSettings.tech_name ||
    techSettings.tech_email ||
    techSettings.tech_phone ||
    techSettings.tech_signature_image;

  const custCutting = (Number(customer.cutting_cost) || 0) * doors;
  const custGluing = (Number(customer.corner_gluing_cost) || 0) * doors;
  const custMounting = (Number(customer.mounting_cost) || 0) * doors;
  const primoWelding = (Number(primo.welding_cutting_cost) || 0) * doors;
  const primoGluing = (Number(primo.gluing_cost) || 0) * doors;
  const primoMounting = (Number(primo.mounting_cost) || 0) * doors;
  const primoGlue = pb.materials;
  const custSteel = cb.steelband;
  const custCuttingMin = ct.cutting * doors;
  const custGluingMin = ct.gluing * doors;
  const custMountingMin = ct.mounting * doors;
  const primoWeldingMin = pt.welding * doors;
  const primoGluingMin = pt.gluing * doors;
  const primoMountingMin = pt.mounting * doors;

  const logoSrc = logoDataUrl || "/brand/primo-logo.png";
  const doorsNoun = tPdf("doors", { count: doors });

  // ---- inline styles ----
  const heroStyle = {
    marginTop: "6mm",
    padding: "8mm 6mm",
    background:
      "linear-gradient(135deg, rgba(0,178,255,0.08) 0%, rgba(0,178,255,0.02) 100%)",
    border: "1.5px solid #00B2FF",
    borderRadius: "2mm",
    textAlign: "center",
  };
  const heroLabel = {
    fontSize: "8pt",
    letterSpacing: "0.22em",
    textTransform: "uppercase",
    color: "#00B2FF",
    fontFamily: "JetBrains Mono, ui-monospace, monospace",
    fontWeight: 600,
  };
  const heroBig = {
    fontSize: "44pt",
    fontWeight: 800,
    lineHeight: 1,
    color: "#00B2FF",
    fontFamily: "Space Grotesk, sans-serif",
    marginTop: "2mm",
  };
  const heroSub = {
    fontSize: "10pt",
    color: "#374151",
    marginTop: "2mm",
    fontFamily: "Inter, sans-serif",
  };
  const compareGrid = {
    marginTop: "6mm",
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "4mm",
  };
  const cardBefore = { background: "#fef2f2", border: "1.5px solid #ef4444", borderRadius: "2mm", padding: "5mm" };
  const cardAfter = { background: "#eff6ff", border: "1.5px solid #00B2FF", borderRadius: "2mm", padding: "5mm" };
  const cardTitle = (color) => ({ fontSize: "8pt", letterSpacing: "0.18em", textTransform: "uppercase", color, fontFamily: "JetBrains Mono, ui-monospace, monospace", fontWeight: 700, marginBottom: "2mm" });
  const cardMetric = (color) => ({ fontSize: "18pt", fontWeight: 800, color, fontFamily: "Space Grotesk, sans-serif", lineHeight: 1 });
  const cardSub = { fontSize: "8.5pt", color: "#6b7280", fontFamily: "JetBrains Mono, ui-monospace, monospace", marginTop: "1mm" };
  const savingsGrid = { marginTop: "6mm", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4mm" };
  const savingsCard = { background: "#ecfdf5", border: "1.5px solid #10b981", borderRadius: "2mm", padding: "5mm" };
  const savingsLabel = { fontSize: "8pt", letterSpacing: "0.18em", textTransform: "uppercase", color: "#047857", fontFamily: "JetBrains Mono, ui-monospace, monospace", fontWeight: 700 };
  const savingsBig = { fontSize: "22pt", fontWeight: 800, color: "#047857", fontFamily: "Space Grotesk, sans-serif", marginTop: "2mm", lineHeight: 1 };
  const savingsSub = { fontSize: "8.5pt", color: "#374151", fontFamily: "JetBrains Mono, ui-monospace, monospace", marginTop: "1mm" };
  const projectionGrid = { marginTop: "5mm", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4mm" };
  const projectionCard = { background: "#0a2540", color: "#ffffff", borderRadius: "2mm", padding: "5mm" };
  const projectionLabel = { fontSize: "7.5pt", letterSpacing: "0.18em", textTransform: "uppercase", color: "#7dd3fc", fontFamily: "JetBrains Mono, ui-monospace, monospace", fontWeight: 700 };
  const projectionBig = { fontSize: "20pt", fontWeight: 800, color: "#ffffff", fontFamily: "Space Grotesk, sans-serif", marginTop: "2mm", lineHeight: 1 };

  return (
    <div className="pdf-doc">
      {/* ---------- Page 1 — Executive summary ---------- */}
      <div className="pdf-accent-bar" />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginTop: "3mm", gap: "10mm" }}>
        <div>
          <img src={logoSrc} alt={tPdf("logoAlt")} style={{ height: "11mm", width: "auto", display: "block" }} />
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: "7.5pt", letterSpacing: "0.2em", textTransform: "uppercase", color: "#00B2FF", fontFamily: "JetBrains Mono, ui-monospace, monospace", fontWeight: 700 }}>
            {tPdf("execTitle")}
          </div>
          <div style={{ fontSize: "13pt", fontWeight: 700, fontFamily: "Space Grotesk, sans-serif", marginTop: "0.5mm" }}>
            {tPdf("headerSubtitle")}
          </div>
          <div style={{ fontSize: "8.5pt", color: "#6b7280", fontFamily: "JetBrains Mono, ui-monospace, monospace", marginTop: "1mm" }}>
            {tPdf("refLabel")} {refId} · {fmt.date(new Date())}
          </div>
        </div>
      </div>

      {/* Client info strip */}
      <div className="pdf-section" style={{ marginTop: "6mm" }}>
        <div className="pdf-kv-grid">
          <div><div className="pdf-kv-label">{tPdf("client")}</div><div className="pdf-kv-value">{client.company_name || "—"}</div></div>
          <div><div className="pdf-kv-label">{tPdf("contact")}</div><div className="pdf-kv-value">{client.contact_name || "—"}</div></div>
          <div><div className="pdf-kv-label">{tPdf("email")}</div><div className="pdf-kv-value" style={{ fontSize: "9pt", wordBreak: "break-all" }}>{client.contact_email || "—"}</div></div>
          <div><div className="pdf-kv-label">{tPdf("production")}</div><div className="pdf-kv-value">{fmt.num(quantity)} · {periodLower}</div></div>
        </div>
      </div>

      {/* Big savings hero */}
      <div style={heroStyle}>
        <div style={heroLabel}>
          {tPdf("execHeroLabel")} · {tPdf("execPerDoors", { n: doors })}
        </div>
        <div style={heroBig}>{fmt.money0(priceDelta)}</div>
        <div style={heroSub}>
          {fmt.percent(reductionPct)} {tPdf("costReduction")}
          {" · "}
          {fmt.hours(timeDeltaH)} {tPdf("savedHours")}
        </div>
      </div>

      {/* Before / After */}
      <div className="pdf-section" style={{ marginTop: "6mm" }}>
        <div className="pdf-section-title">{tPdf("execCompareTitle")}</div>
        <div style={compareGrid}>
          <div style={cardBefore}>
            <div style={cardTitle("#b91c1c")}>{tPdf("beforePrimo")}</div>
            <div style={cardMetric("#b91c1c")}>{fmt.money(priceCurrent)}</div>
            <div style={cardSub}>{tPdf("cost")}</div>
            <div style={{ ...cardMetric("#b91c1c"), fontSize: "14pt", marginTop: "3mm" }}>
              {fmt.minAndHours(timeCurrentMin)}
            </div>
            <div style={cardSub}>{tPdf("time")}</div>
          </div>
          <div style={cardAfter}>
            <div style={cardTitle("#00B2FF")}>{tPdf("afterPrimo")}</div>
            <div style={cardMetric("#00B2FF")}>{fmt.money(pricePrimo)}</div>
            <div style={cardSub}>{tPdf("cost")}</div>
            <div style={{ ...cardMetric("#00B2FF"), fontSize: "14pt", marginTop: "3mm" }}>
              {fmt.minAndHours(timePrimoMin)}
            </div>
            <div style={cardSub}>{tPdf("time")}</div>
          </div>
        </div>
      </div>

      {/* Cost / Time savings — green cards */}
      <div style={savingsGrid}>
        <div style={savingsCard}>
          <div style={savingsLabel}>{tPdf("costSavings")}</div>
          <div style={savingsBig}>{fmt.money(priceDelta)}</div>
          <div style={savingsSub}>-{fmt.percent(reductionPct)}</div>
        </div>
        <div style={savingsCard}>
          <div style={savingsLabel}>{tPdf("timeSavings")}</div>
          <div style={savingsBig}>{fmt.hours(timeDeltaH)}</div>
          <div style={savingsSub}>-{fmt.percent(timeReductionPct)}</div>
        </div>
      </div>

      {/* Projection banners */}
      <div style={projectionGrid}>
        <div style={projectionCard}>
          <div style={projectionLabel}>
            {tPdf("projectionPeriod", { period: periodLabel })}
          </div>
          <div style={projectionBig}>{fmt.money0(periodSavings)}</div>
          <div style={{ ...savingsSub, color: "#93c5fd", marginTop: "2mm" }}>
            {fmt.num(quantity)} × {fmt.num(doors)} {doorsNoun} · {periodLower}
          </div>
        </div>
        <div style={projectionCard}>
          <div style={projectionLabel}>{tPdf("projectionAnnual")}</div>
          <div style={projectionBig}>{fmt.money0(annualSavings)}</div>
          <div style={{ ...savingsSub, color: "#93c5fd", marginTop: "2mm" }}>
            {tPdf("fleetTotalSavings")}
          </div>
        </div>
      </div>

      {/* Footer / brand strip on page 1 (signature is now on the LAST page, per user request) */}
      <div className="pdf-footer">
        <div>{tPdf("brandLine")}</div>
        <div>{tPdf("refLabel")} {refId} · {fmt.date(new Date())}</div>
      </div>

      {/* ---------- Page 2 — Technical detail ---------- */}
      <div className="pdf-page-break" />
      <div className="pdf-accent-bar" />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginTop: "3mm", gap: "10mm" }}>
        <div>
          <img src={logoSrc} alt={tPdf("logoAlt")} style={{ height: "11mm", width: "auto", display: "block" }} />
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: "7.5pt", letterSpacing: "0.2em", textTransform: "uppercase", color: "#00B2FF", fontFamily: "JetBrains Mono, ui-monospace, monospace", fontWeight: 700 }}>
            {tPdf("technicalDetail")}
          </div>
          <div style={{ fontSize: "13pt", fontWeight: 700, fontFamily: "Space Grotesk, sans-serif", marginTop: "0.5mm" }}>
            {tPdf("headerSubtitle")}
          </div>
          <div style={{ fontSize: "8.5pt", color: "#6b7280", fontFamily: "JetBrains Mono, ui-monospace, monospace", marginTop: "1mm" }}>
            {tPdf("refLabel")} {refId} · {fmt.date(new Date())}
          </div>
        </div>
      </div>

      {/* §3 Cost breakdown */}
      <div className="pdf-section" style={{ marginTop: "5mm" }}>
        <div className="pdf-section-title">{tPdf("s3")}</div>
        <table className="pdf-table">
          <thead>
            <tr>
              <th style={{ width: "40%" }}>{tPdf("component")}</th>
              <th className="num">{tPdf("customer")}</th>
              <th className="num">{tPdf("primo")}</th>
              <th className="num">{tPdf("savings")}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{tPdf("leftDoorProfiles")}</td>
              <td className="num">{fmt.money(cb.frameAProfiles)}</td>
              <td className="num">{fmt.money(pb.frameAProfiles)}</td>
              <td className="num pdf-good">{fmt.money(cb.frameAProfiles - pb.frameAProfiles)}</td>
            </tr>
            <tr>
              <td>{tPdf("rightDoorProfiles")}</td>
              <td className="num">{fmt.money(cb.frameBProfiles)}</td>
              <td className="num">{fmt.money(pb.frameBProfiles)}</td>
              <td className="num pdf-good">{fmt.money(cb.frameBProfiles - pb.frameBProfiles)}</td>
            </tr>
            <tr>
              <td>{tPdf("cuttingWelding")}</td>
              <td className="num">{fmt.money(custCutting)}</td>
              <td className="num">{fmt.money(primoWelding)}</td>
              <td className="num pdf-good">{fmt.money(custCutting - primoWelding)}</td>
            </tr>
            <tr>
              <td>{tPdf("cornerGluing")}</td>
              <td className="num">{fmt.money(custGluing)}</td>
              <td className="num">{fmt.money(primoGluing)}</td>
              <td className="num pdf-good">{fmt.money(custGluing - primoGluing)}</td>
            </tr>
            <tr>
              <td>{tPdf("mounting")}</td>
              <td className="num">{fmt.money(custMounting)}</td>
              <td className="num">{fmt.money(primoMounting)}</td>
              <td className="num pdf-good">{fmt.money(custMounting - primoMounting)}</td>
            </tr>
            <tr>
              <td>{tPdf("steelbandRow")}</td>
              <td className="num">{fmt.money(custSteel)}</td>
              <td className="num pdf-good">{tPdf("eliminated")}</td>
              <td className="num pdf-good">{fmt.money(custSteel)}</td>
            </tr>
            <tr>
              <td>{tPdf("glueRow")}</td>
              <td className="num">—</td>
              <td className="num">{fmt.money(primoGlue)}</td>
              <td className="num pdf-danger">-{fmt.money(primoGlue)}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td>{tPdf("totalSet", { n: doors })}</td>
              <td className="num">{fmt.money(priceCurrent)}</td>
              <td className="num">{fmt.money(pricePrimo)}</td>
              <td className="num pdf-accent">
                {fmt.money(priceDelta)} ({fmt.percent(reductionPct)})
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* §4 Labour time */}
      <div className="pdf-section">
        <div className="pdf-section-title">{tPdf("s4")}</div>
        <table className="pdf-table">
          <thead>
            <tr>
              <th style={{ width: "40%" }}>{tPdf("operation")}</th>
              <th className="num">{tPdf("customer")}</th>
              <th className="num">{tPdf("primo")}</th>
              <th className="num">{tPdf("savings")}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{tPdf("cuttingWelding")}</td>
              <td className="num">{fmt.minAndHours(custCuttingMin)}</td>
              <td className="num">{fmt.minAndHours(primoWeldingMin)}</td>
              <td className="num pdf-good">{fmt.minAndHours(custCuttingMin - primoWeldingMin)}</td>
            </tr>
            <tr>
              <td>{tPdf("cornerGluing")}</td>
              <td className="num">{fmt.minAndHours(custGluingMin)}</td>
              <td className="num">{fmt.minAndHours(primoGluingMin)}</td>
              <td className="num pdf-good">{fmt.minAndHours(custGluingMin - primoGluingMin)}</td>
            </tr>
            <tr>
              <td>{tPdf("mounting")}</td>
              <td className="num">{fmt.minAndHours(custMountingMin)}</td>
              <td className="num">{fmt.minAndHours(primoMountingMin)}</td>
              <td className="num pdf-good">{fmt.minAndHours(custMountingMin - primoMountingMin)}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td>{tPdf("totalLabour")}</td>
              <td className="num">{fmt.minAndHours(timeCurrentMin)}</td>
              <td className="num">{fmt.minAndHours(timePrimoMin)}</td>
              <td className="num pdf-accent">
                {fmt.minAndHours(timeCurrentMin - timePrimoMin)} ({fmt.percent(timeReductionPct)})
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Footer of page 2 */}
      <div className="pdf-footer">
        <div>{tPdf("brandLine")}</div>
        <div>
          {tPdf("refLabel")} {refId} · {fmt.date(new Date())} · {tPdf("profile")} {fmt.num(thickness)}mm
        </div>
      </div>
    </div>
  );
}
